/**
 * Utility to determine if a market is currently open based on Indian Standard Time (IST).
 */

/**
 * Returns current time in IST formatted to allow easy parsing.
 * @returns {Object} { dayOfWeek: number, hours: number, minutes: number }
 */
const getCurrentISTTime = () => {
  const now = new Date();
  
  // Format the date specifically to IST timezone
  const options = {
    timeZone: 'Asia/Kolkata',
    hour12: false,
    weekday: 'long',
    hour: 'numeric',
    minute: 'numeric',
  };
  
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);
  
  let weekday = '';
  let hours = 0;
  let minutes = 0;
  
  parts.forEach(part => {
    if (part.type === 'weekday') weekday = part.value;
    if (part.type === 'hour') hours = parseInt(part.value, 10);
    if (part.type === 'minute') minutes = parseInt(part.value, 10);
  });
  
  // Convert weekday to 0-6 index (Sunday = 0)
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayOfWeek = days.indexOf(weekday);
  
  // Handle edge case where 24:00 is returned instead of 00:00 depending on environment
  if (hours === 24) hours = 0;
  
  return { dayOfWeek, hours, minutes };
};

// Parses a 24-hour "HH:mm" time (as sent by the session info API) into minutes from midnight.
// Returns null for a missing or malformed value.
const timeToMinutes = (timeStr) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeStr || '');
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return (hours * 60) + minutes;
};

/**
 * Checks if a given market schedule is currently active.
 * 
 * @param {string} scheduleType - 'daily' (Mon - Fri) or 'daily_all_days'
 * @param {string} [openTime] - e.g., "09:15"
 * @param {string} [closeTime] - e.g., "15:30"
 * @returns {boolean} true if the market is open, false otherwise.
 */
export const isMarketOpen = (scheduleType, openTime, closeTime) => {
  const { dayOfWeek, hours, minutes } = getCurrentISTTime();
  const currentMinutes = (hours * 60) + minutes;
  const startMinutes = timeToMinutes(openTime);
  const endMinutes = timeToMinutes(closeTime);

  // A session belongs to the day it opens: before today's open time, an overnight
  // window (e.g., 20:00 -> 02:30) is still yesterday's session, so Sat 01:00 is Friday's
  const isPrevDaySession = startMinutes >= endMinutes && currentMinutes < startMinutes;
  const sessionDay = isPrevDaySession ? (dayOfWeek + 6) % 7 : dayOfWeek;
  const isSessionOnWeekend = sessionDay === 0 || sessionDay === 6; // Sunday(0), Saturday(6)

  if (isSessionOnWeekend && scheduleType !== 'daily_all_days') {
    return false;
  }

  // Handle normal daytime hours (e.g., 09:15 -> 15:30)
  if (startMinutes < endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  } 
  
  // Handle overnight hours crossing midnight (e.g., 20:00 -> 02:30)
  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

// Market opens at amo_end_time and closes at post_closing_end of the matching session.
// Returns null when the session or either time is missing, so the row shows "-".
export const getSessionTiming = (sessionName, sessions) => {
  const session = sessions?.find((s) => s?.session_name === sessionName);
  const open = session?.amo_end_time;
  const close = session?.post_closing_end;

  if (timeToMinutes(open) === null || timeToMinutes(close) === null) return null;

  return { open, close, timeDisplay: `${open} - ${close}` };
};
